import { useEffect, useState } from 'react';

import { BrowserSupportModal, Constants, usePlatformService } from 'core';
import { useFindOrCreateUserFromEmail } from 'core/Api';
import { useSearchParams } from 'react-router-dom';
import { Box, ButtonMantine, Container, Group, Stack, Text, TextInput, Title } from 'ui';
import { isEmail, useForm } from 'ui/Form';
import { IconGivebacks } from 'ui/Icons';

import { useEmailStorage } from './hooks';

const useEmailConfirmation = () => {
  const { setLocalStorageEmail } = useEmailStorage();

  const [searchParams] = useSearchParams();
  const emailFromParams = searchParams.get('email');

  const form = useForm({
    initialValues: {
      email: '',
    },
    validate: {
      email: isEmail('Please enter a valid email address'),
    },
  });

  useEffect(() => {
    // Guard against empty email
    if (!emailFromParams) return;
    form.setFieldValue('email', emailFromParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on email param changes, not form changes
  }, [emailFromParams]);

  return {
    email: form.values.email,
    setEmail: (email: string) => form.setFieldValue('email', email),
    emailInputProps: form.getInputProps('email'),
    validateForm: () => form.validate(),
    setLocalStorageEmail,
  };
};

const MobileBrowserContent = () => {
  const { email } = useEmailConfirmation();

  const Platform = usePlatformService();

  const handleGetApp = () => {
    if (Platform.mobileBrowser == 'ios') {
      window.location.href = Constants.AppLinks.appStore;
    } else {
      window.location.href = Constants.AppLinks.android_play_store;
    }
  };

  return (
    <Container size="xs" mt={48}>
      <Stack gap={48} ta={'center'}>
        <Stack gap={'xs'}>
          <Title order={3}>Download the Givebacks App</Title>
          <Text type="sub" c="gray">
            <Text>
              Download the app and use your email address (
              <Box c="cobalt" component="span">
                {email}
              </Box>
              ) to register. The app will then walk you through adding the Givebacks mobile
              extension.
            </Text>
          </Text>
        </Stack>
        <ButtonMantine onClick={handleGetApp}>Download the App</ButtonMantine>
      </Stack>
    </Container>
  );
};

const DesktopBrowserContent = () => {
  const { email, emailInputProps, validateForm, setLocalStorageEmail } = useEmailConfirmation();
  const { mutate: findOrCreateUser, isPending: isFindLoading } = useFindOrCreateUserFromEmail();

  const [showBrowserSupportModal, setShowBrowserSupportModal] = useState(false);
  const Platform = usePlatformService();

  // const navigate = useNavigate();  // For testing below
  const handleEmailVerification = () => {
    if (Platform.OS == 'brave' || Platform.OS == 'chrome' || Platform.OS == 'chromium') {
      if (validateForm().hasErrors) return;
      findOrCreateUser(email, {
        onSuccess() {
          setLocalStorageEmail(email);
          window.location.href = Constants.chromeWebsStore;
          // navigate('/chrome-extension'); // When testing below, we can use this
        },
      });
    } else {
      setShowBrowserSupportModal(true);
      return;
    }
  };

  return (
    <Container size="xs" mt={48}>
      <Stack gap={48}>
        <Stack gap={'xs'}>
          <Title order={3}>Confirm your email address to add the Givebacks extension</Title>
          <Text type="sub" c="gray">
            Once you confirm, we'll redirect you to the Chrome Store to install the Givebacks
            extension.
          </Text>
        </Stack>
        <Group wrap="nowrap" align="flex-start">
          <TextInput
            w="100%"
            placeholder="Email Address"
            leftSection={<IconGivebacks />}
            {...emailInputProps}
          />
          <ButtonMantine
            size="md"
            onClick={handleEmailVerification}
            disabled={isFindLoading}
            loading={isFindLoading}
          >
            Confirm
          </ButtonMantine>
        </Group>
      </Stack>

      <BrowserSupportModal
        opened={showBrowserSupportModal}
        onClose={() => setShowBrowserSupportModal(false)}
      />
    </Container>
  );
};

const Experiment_EmailConfirmation = () => {
  const Platform = usePlatformService();
  const isMobileBrowser = Platform.mobileBrowser;

  if (isMobileBrowser) return <MobileBrowserContent />;
  return <DesktopBrowserContent />;
};

export { Experiment_EmailConfirmation };
